import { useState, type ReactNode } from "react";
import { ImagePlus } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { ResolvedBriefVisual } from "@/types/productionBrief.type";
import { posterFrameUrl } from "../../videoThumbnails";

/** Who is reading the brief. The client portal (batch 3) passes "client". */
export type BriefAudience = "team" | "client";

/** The placeholder an empty Arabic field shows. Not written here; it is the product's existing string. */
export const ARABIC_NOT_WRITTEN = "لم يُكتب بعد";

/** Stand-in fallback until cockpit.css carries --co-tonal. */
export const TONAL_BG = "bg-[var(--co-tonal,rgba(255,255,255,.05))]";

/**
 * The brief's section colours (Figma 08, "Accent" on each section): Treatment
 * blue, the picture and the shot list indigo, inputs and art direction purple,
 * the words amber.
 */
export type BriefAccent = "blue" | "indigo" | "purple" | "amber";
const ACCENT: Record<BriefAccent, string> = {
  blue: "var(--co-pillar-2)",
  indigo: "var(--co-pillar-3)",
  purple: "var(--co-pillar-1)",
  amber: "#d9a441",
};

/** A section colour as a CSS value. */
export function briefAccentColor(accent: BriefAccent): string {
  return ACCENT[accent];
}

/** A section: a 3px rule in its colour down the left, the content 16 in from it. */
export function BriefSection({ accent, className, children }: { accent: BriefAccent; className?: string; children: ReactNode }) {
  return (
    <section className={cn("min-w-0 border-l-[3px] pl-4", className)} style={{ borderLeftColor: ACCENT[accent] }}>
      {children}
    </section>
  );
}

/**
 * Section head: the title as a tag in the section's colour (28% under it,
 * Barlow Condensed 700 22), optional muted sub, a bottom hairline that takes no
 * space. 12 under the title, then 18 to the content; `tight` is the inputs and
 * captions head (8 under, 12 to the content).
 */
export function SectionHead({
  title,
  accent,
  sub,
  aside,
  id,
  tight = false,
  className,
}: {
  title: string;
  accent: BriefAccent;
  sub?: string | null;
  aside?: ReactNode;
  id?: string;
  tight?: boolean;
  className?: string;
}) {
  return (
    <header
      className={cn(
        "flex items-center justify-between gap-3 shadow-[inset_0_-1px_0_var(--co-line)]",
        tight ? "mb-3 pb-2" : "mb-[18px] pb-3",
        className,
      )}
    >
      <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        <h3
          id={id}
          className="inline-flex rounded-[6px] px-2.5 py-0.5 font-section text-[22px] font-bold leading-[26px] tracking-normal text-co-text"
          style={{ background: `color-mix(in srgb, ${ACCENT[accent]} 28%, transparent)` }}
        >
          {title}
        </h3>
        {sub ? <span className="text-[13.5px] text-co-muted">{sub}</span> : null}
      </span>
      {aside ? <span className="shrink-0 text-right text-[12.5px] text-co-muted">{aside}</span> : null}
    </header>
  );
}

/** A 12.5 muted label. */
export function FieldLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn("text-[12.5px] leading-snug text-co-muted", className)}>{children}</p>;
}

/** Bullets: a 5px square in --co-text2, text 13.5 --co-text2. */
export function BulletList({ items, className }: { items: string[]; className?: string }) {
  return (
    <ul className={cn("flex flex-col gap-2", className)}>
      {items.map((item, index) => (
        <li
          key={`${item}-${index}`}
          className="relative pl-[13px] text-[13.5px] leading-[1.4] text-co-text2"
        >
          <span className="absolute left-0 top-[0.55em] size-[5px] bg-co-text2" aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function isDirectImageUrl(value?: string): boolean {
  const trimmed = value?.trim() ?? "";
  return /^data:image\//i.test(trimmed) || /\.(?:avif|gif|jpe?g|png|webp)(?:[?#].*)?$/i.test(trimmed);
}

export interface BriefPictureSource {
  kind: "image" | "video";
  src: string;
  poster?: string;
  /** Not final media: a reference, a trend example or a pasted link. */
  standIn: boolean;
}

/**
 * The shot's or slide's picture, resolved the same way the old doc did: a
 * picked visual (`resolvedVisual`) wins over a pasted `referenceUrl`, and the
 * pasted one only counts when it is a direct image. A picture from the brand's
 * media library is final media; every other source is a stand-in.
 */
export function resolveBriefPicture(
  resolved?: ResolvedBriefVisual,
  referenceUrl?: string,
): BriefPictureSource | null {
  if (resolved && !resolved.unavailable) {
    const standIn = resolved.source?.kind !== "media";
    const first = resolved.items?.[0];
    if (first?.url) {
      return first.mediaType === "video"
        ? { kind: "video", src: first.url, poster: first.posterUrl ?? undefined, standIn }
        : { kind: "image", src: first.url, standIn };
    }
    const still = resolved.imageUrl ?? resolved.thumbnailUrl ?? "";
    if (resolved.mediaType === "video" && resolved.videoUrl) {
      return { kind: "video", src: resolved.videoUrl, poster: still || undefined, standIn };
    }
    if (still) return { kind: "image", src: still, standIn };
    if (resolved.videoUrl) return { kind: "video", src: resolved.videoUrl, standIn };
  }
  const pasted = referenceUrl?.trim() ?? "";
  if (pasted && isDirectImageUrl(pasted)) return { kind: "image", src: pasted, standIn: true };
  return null;
}

/**
 * A picture box of a fixed size. No picture: a recess with "No picture yet".
 * `fallback` replaces that empty state (the type-only slide, or the editor's
 * Choose a picture). With `onChange` a filled picture carries a Change button.
 *
 * A picture's links are signed per read, so every save and refetch hands the
 * same picture a new URL. The new one loads under the last painted one, and
 * swaps in once it has loaded, instead of blanking the box while it loads.
 */
export function BriefPicture({
  picture,
  width,
  height,
  alt,
  fallback,
  fluid = false,
  className,
  onChange,
  changeLabel,
}: {
  picture: BriefPictureSource | null;
  width: number;
  height: number;
  alt: string;
  fallback?: ReactNode;
  /** Fill the parent's width and keep the ratio, instead of a fixed box. */
  fluid?: boolean;
  className?: string;
  /** Present when the reader can save a pick: a filled picture can be changed. */
  onChange?: () => void;
  changeLabel?: string;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const [painted, setPainted] = useState<string | null>(null);
  const broken = !!picture && failed === picture.src;
  const shown = !!picture && !broken;
  // The image still loading sits over the last one painted until it is ready.
  const swapping = shown && picture.kind === "image" && !!painted && painted !== picture.src;
  const style = fluid ? { aspectRatio: `${width} / ${height}` } : { width, height };
  return (
    <div
      className={cn("group/pic relative shrink-0 overflow-hidden rounded-card", shown && "bg-co-recess", fluid && "w-full", className)}
      style={style}
    >
      {shown ? (
        picture.kind === "video" ? (
          <video
            src={posterFrameUrl(picture.src)}
            poster={picture.poster}
            muted
            playsInline
            preload="metadata"
            aria-label={alt}
            className="size-full object-cover"
          />
        ) : (
          <>
            {swapping ? (
              <img
                src={painted}
                alt=""
                aria-hidden="true"
                referrerPolicy="no-referrer"
                className="absolute inset-0 size-full object-cover"
              />
            ) : null}
            <img
              src={picture.src}
              alt={alt}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onLoad={() => setPainted(picture.src)}
              onError={() => setFailed(picture.src)}
              className={cn("relative size-full object-cover", swapping && "opacity-0")}
            />
          </>
        )
      ) : fallback ? (
        fallback
      ) : (
        <div className="grid size-full place-items-center bg-co-recess px-2 text-center">
          <span className="text-[12.5px] text-co-muted">No picture yet</span>
        </div>
      )}
      {shown && picture.standIn ? (
        <span className="absolute bottom-2 left-2 rounded-tag bg-co-scrim px-[7px] py-[3px] text-[12px] leading-[1.3] text-white">
          Stand-in
        </span>
      ) : null}
      {shown && onChange ? (
        // The idea slide's Change media, at the brief's size: on hover, always on touch.
        <button
          type="button"
          onClick={onChange}
          aria-label={changeLabel ?? `Change the picture: ${alt}`}
          className="absolute right-2 top-2 inline-flex h-7 items-center gap-1.5 rounded-[7px] bg-[#121212]/60 px-2 text-[12.5px] font-medium text-[#fafafa] opacity-0 backdrop-blur outline-none transition-opacity group-hover/pic:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-co-accent [@media(hover:none)]:opacity-100"
        >
          <ImagePlus className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
          Change
        </button>
      ) : null}
    </div>
  );
}

/**
 * An empty picture box an editor can fill: opens the picture picker (Brand
 * media, Trend, References, Upload). Only shown when the caller can save a
 * pick; a read-only reader keeps the plain empty state.
 */
export function ChooseVisualTile({ label, onChoose }: { label: string; onChoose: () => void }) {
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-label={label}
      className="group grid size-full place-items-center rounded-card border border-dashed border-co-line2 bg-co-recess px-2 text-center outline-none transition-colors hover:border-co-accent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent"
    >
      <span className="flex flex-col items-center gap-1.5 text-co-muted group-hover:text-co-text">
        <ImagePlus className="size-5" strokeWidth={1.6} aria-hidden="true" />
        <span className="text-[12.5px] font-medium">Choose a picture</span>
      </span>
    </button>
  );
}
